import api from './api';

// Calendar types
export interface CalendarAccount {
  id: string;
  email: string;
  provider: string;
  is_primary: boolean;
  created_at: string;
}

export interface Calendar {
  id: string;
  calendar_account_id: string;
  calendar_id: string;
  calendar_name: string;
  description?: string;
  color?: string;
  is_primary: boolean;
  is_enabled: boolean;
  include_in_availability: boolean;
  timezone?: string;
  access_role?: string;
  created_at: string;
  updated_at: string;
  account_email?: string;
  provider?: string;
}

export type EventType = 'meeting' | 'time_block' | 'focus_time';

export interface CalendarEvent {
  id: string;
  calendar_account_id: string;
  event_id: string;
  title: string;
  description?: string;
  location?: string;
  meeting_url?: string;
  start_time: string;
  end_time: string;
  attendees?: Array<string | { email: string; name?: string; response_status?: string }>;
  is_all_day: boolean;
  status: string;
  created_at: string;
  updated_at: string;
  // Calendar metadata (from enhanced API)
  calendar_id?: string;
  calendar_name?: string;
  calendar_color?: string;
  account_email?: string;
  provider?: string;
  // Display-time dedup (backend merges near-duplicates across accounts)
  merged_count?: number;
  merged_calendars?: string[];
  // Time blocking fields
  event_type: EventType;
  task_id?: string;
  task?: {
    id: string;
    title: string;
    status: 'todo' | 'in_progress' | 'completed';
    priority: number;
    due_date?: string;
  }; // Nested task for time blocks
  // Document integration
  documents?: Array<{
    id: string;
    name: string;
    mime_type: string;
    size_bytes: number;
    thumbnail_url: string | null;
    created_at: string;
  }>;
}

export interface CreateEventRequest {
  title: string;
  description?: string;
  location?: string;
  start_time: string;
  end_time: string;
  attendees?: string[];
  is_all_day?: boolean;
  timezone?: string;
  calendar_account_id?: string;
  calendar_id?: string;
}

export interface UpdateEventRequest {
  title?: string;
  description?: string;
  location?: string;
  start_time?: string;
  end_time?: string;
  attendees?: string[];
  is_all_day?: boolean;
}

export interface SyncCalendarsResponse {
  message: string;
  results: Array<{
    calendar_account_id: string;
    calendar_name: string;
    provider: string;
    status: string;
    result?: unknown;
    error?: string;
  }>;
}

export interface GetEventsResponse {
  events: CalendarEvent[];
  pagination: {
    page: number;
    per_page: number;
    total_pages: number;
    total_items: number;
    has_next: boolean;
    has_prev: boolean;
  };
}

// Calendar API methods
export interface SyncCalendarsRequest {
  calendar_account_id?: string;
  days_back?: number;
  days_forward?: number;
}

export const syncCalendars = async (options?: SyncCalendarsRequest): Promise<SyncCalendarsResponse> => {
  const response = await api.post<SyncCalendarsResponse>('/api/v1/calendars/sync', options || {});
  return response.data;
};

export const getEvents = async (
  startDate: string,
  endDate: string,
  emailAccountId?: string,
  page?: number
): Promise<GetEventsResponse> => {
  const params = new URLSearchParams({
    start_date: startDate,
    end_date: endDate,
  });

  if (emailAccountId) {
    params.append('email_account_id', emailAccountId);
  }

  if (page) {
    params.append('page', String(page));
  }

  const response = await api.get<GetEventsResponse>(`/api/v1/calendars/events?${params.toString()}`);
  return response.data;
};

export const getEvent = async (id: string): Promise<CalendarEvent> => {
  const response = await api.get<CalendarEvent>(`/api/v1/calendars/events/${id}`);
  return response.data;
};

export const createEvent = async (event: CreateEventRequest): Promise<CalendarEvent> => {
  const response = await api.post<CalendarEvent>('/api/v1/calendars/events', event);
  return response.data;
};

// Copy only the subject + date/time of a source event into another (or the same)
// calendar. Intentionally omits description, location, attendees, meeting_url,
// recurrence, and documents.
export const duplicateEventToCalendar = async (
  source: CalendarEvent,
  target: { calendar_account_id: string; calendar_id: string },
  timezone?: string
): Promise<CalendarEvent> => {
  return createEvent({
    title: source.title,
    start_time: source.start_time,
    end_time: source.end_time,
    is_all_day: source.is_all_day,
    timezone,
    calendar_account_id: target.calendar_account_id,
    calendar_id: target.calendar_id,
  });
};

export const updateEvent = async (id: string, event: UpdateEventRequest): Promise<CalendarEvent> => {
  const response = await api.put<CalendarEvent>(`/api/v1/calendars/events/${id}`, event);
  return response.data;
};

export const deleteEvent = async (id: string): Promise<void> => {
  await api.delete(`/api/v1/calendars/events/${id}`);
};

export const listCalendars = async (): Promise<{ calendars: Calendar[]; total: number }> => {
  const response = await api.get<{ calendars: Calendar[]; total: number }>('/api/v1/calendars/list');
  return response.data;
};

export const updateCalendar = async (
  id: string,
  updates: { is_enabled?: boolean; include_in_availability?: boolean; calendar_name?: string }
): Promise<{ message: string; calendar: Calendar }> => {
  const response = await api.put<{ message: string; calendar: Calendar }>(
    `/api/v1/calendars/${id}`,
    updates
  );
  return response.data;
};

export interface RefreshCalendarsResponse {
  message: string;
  added: number;
  updated: number;
  total: number;
}

export const refreshCalendars = async (
  calendarAccountId?: string
): Promise<RefreshCalendarsResponse> => {
  const response = await api.post<RefreshCalendarsResponse>(
    '/api/v1/calendars/refresh',
    calendarAccountId ? { calendar_account_id: calendarAccountId } : {}
  );
  return response.data;
};

export interface InitCalendarsResponse {
  message: string;
  created: number;
  calendars_added: number;
}

export const initCalendars = async (): Promise<InitCalendarsResponse> => {
  const response = await api.post<InitCalendarsResponse>('/api/v1/calendars/init');
  return response.data;
};

export interface BulkUpdateCalendarsRequest {
  calendar_ids?: string[];
  calendar_account_id?: string;
  is_enabled: boolean;
}

export interface BulkUpdateCalendarsResponse {
  message: string;
  updated: number;
  is_enabled: boolean;
}

export const bulkUpdateCalendars = async (
  request: BulkUpdateCalendarsRequest
): Promise<BulkUpdateCalendarsResponse> => {
  const response = await api.put<BulkUpdateCalendarsResponse>(
    '/api/v1/calendars/bulk-update',
    request
  );
  return response.data;
};

// Quick Availability types and API
export interface DateRange {
  start: string; // YYYY-MM-DD format
  end: string;   // YYYY-MM-DD format
}

export interface AvailabilitySlot {
  start: string; // HH:MM format
  end: string;   // HH:MM format
}

export interface DayAvailability {
  date: string;
  day_name: string;
  free_slots: AvailabilitySlot[];
}

export interface AvailabilitySummary {
  total_days: number;
  days_with_availability: number;
  total_free_hours: number;
}

export interface GetAvailabilityRequest {
  date_ranges: DateRange[];
  working_hours_start?: string; // HH:MM, default "08:00"
  working_hours_end?: string;   // HH:MM, default "16:00"
  slot_duration_minutes?: number; // Minimum slot duration, default 30
  timezone?: string; // IANA timezone string, e.g., "America/Los_Angeles"
}

export interface GetAvailabilityResponse {
  availability_text: string;
  slots: DayAvailability[];
  summary: AvailabilitySummary;
  timezone?: string; // IANA timezone string
  timezone_abbrev?: string; // Timezone abbreviation, e.g., "PST"
}

export const getAvailability = async (
  request: GetAvailabilityRequest
): Promise<GetAvailabilityResponse> => {
  const response = await api.post<GetAvailabilityResponse>(
    '/api/v1/calendars/availability',
    request
  );
  return response.data;
};

// ============ Document-Calendar Integration ============

export interface DocumentSuggestion {
  document: {
    id: string;
    name: string;
    mime_type: string;
    size_bytes: number;
    thumbnail_url: string | null;
    created_at: string;
    tags: string[];
  };
  reason: string;
  confidence: number;
}

export interface GetSuggestedDocumentsResponse {
  suggestions: DocumentSuggestion[];
}

/**
 * Link a document to a calendar event
 */
export const linkDocumentToEvent = async (
  eventId: string,
  documentId: string
): Promise<CalendarEvent> => {
  const response = await api.post<CalendarEvent>(
    `/api/v1/calendars/events/${eventId}/documents`,
    { document_id: documentId }
  );
  return response.data;
};

/**
 * Unlink a document from a calendar event
 */
export const unlinkDocumentFromEvent = async (
  eventId: string,
  documentId: string
): Promise<CalendarEvent> => {
  const response = await api.delete<CalendarEvent>(
    `/api/v1/calendars/events/${eventId}/documents/${documentId}`
  );
  return response.data;
};

/**
 * Get AI-suggested documents for a calendar event
 */
export const getSuggestedDocuments = async (
  eventId: string
): Promise<DocumentSuggestion[]> => {
  const response = await api.get<GetSuggestedDocumentsResponse>(
    `/api/v1/calendars/events/${eventId}/suggested-documents`
  );
  return response.data.suggestions;
};
